/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Stack, Typography } from "@mui/material";
import {
  useVideoControlActions,
  useVideoControlState,
} from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useTenantTranslation } from "hooks/formatters";
import { useEffect, useRef, useState } from "react";

type VideoSnapshotsPreviewProps = Readonly<{
  compact?: boolean;
}>;

/**
 * Displays current frame snapshots from all active video players.
 * Must be used within a VideoControlProvider context.
 */
export const VideoSnapshotsPreview = ({
  compact = false,
}: VideoSnapshotsPreviewProps) => {
  const { t } = useTenantTranslation();
  const state = useVideoControlState();
  const actions = useVideoControlActions();
  const [snapshots, setSnapshots] = useState<
    { id: string; dataUrl: string | null; label: string }[]
  >([]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const videoRefsMap = actions.getVideoRefs();

    if (videoRefsMap.size === 0) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect -- clears snapshots when no video refs available
      setSnapshots([]);
      return;
    }

    canvasRef.current ??= document.createElement("canvas");
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const capturedSnapshots: {
      id: string;
      dataUrl: string | null;
      label: string;
    }[] = [];

    videoRefsMap.forEach((videoElement: HTMLVideoElement, videoId: string) => {
      let label = videoId;

      // Labels recognise `location-angle` and `device-X-camera-Y` identifiers.
      if (videoId.includes("device-")) {
        const parts = videoId.split("-");
        const cameraIndex = parts.findIndex((p) => p === "camera");
        if (cameraIndex !== -1 && parts[cameraIndex + 1]) {
          label = `Camera ${parts[cameraIndex + 1] ?? ""}`;
        }
      } else {
        const parts = videoId.split("-");
        if (parts.length >= 2) {
          label = parts[parts.length - 1] ?? videoId;
        }
      }

      if (
        !videoElement ||
        videoElement.readyState < 2 ||
        videoElement.videoWidth === 0
      ) {
        capturedSnapshots.push({
          id: videoId,
          dataUrl: null,
          label,
        });
        return;
      }

      try {
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;

        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

        const dataUrl = canvas.toDataURL("image/jpeg", 0.8);

        capturedSnapshots.push({
          id: videoId,
          dataUrl,
          label,
        });
      } catch (error) {
        console.warn(`Failed to capture snapshot for ${videoId}:`, error);
        capturedSnapshots.push({
          id: videoId,
          dataUrl: null,
          label,
        });
      }
    });

    setSnapshots(capturedSnapshots);
  }, [state.currentTime, actions]);

  if (snapshots.length === 0) {
    return null;
  }

  return (
    <Stack spacing={compact ? 1 : 2}>
      {!compact && (
        <Typography variant="body2" color="text.secondary">
          {t("currentVideoFrames")}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: compact
            ? "repeat(auto-fit, minmax(120px, 1fr))"
            : "repeat(auto-fit, minmax(200px, 1fr))",
          gap: compact ? 1 : 2,
          overflowY: "auto",
          justifyContent: "center",
          alignItems: "stretch",
        }}
      >
        {snapshots.map((snapshot) => (
          <Box
            key={snapshot.id}
            sx={{
              position: "relative",
              borderRadius: 1,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "black",
              aspectRatio: snapshot.dataUrl ? "auto" : "16 / 9",
              minHeight: snapshot.dataUrl
                ? "auto"
                : compact
                  ? "67.5px"
                  : "112.5px",
            }}
          >
            {snapshot.dataUrl ? (
              <img
                src={snapshot.dataUrl}
                alt={snapshot.label}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            )}
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: 4,
                left: 4,
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                px: 1,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: compact ? "0.65rem" : "0.7rem",
                fontWeight: "fontWeightMedium",
              }}
            >
              {snapshot.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};
